import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Leaf, LogOut, Plus, Pencil, Trash2, Globe } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/api";

const LANGS = ["cs", "ru", "en"];
const emptyTr = () => ({ cs: { title: "", excerpt: "", content: "" }, ru: { title: "", excerpt: "", content: "" }, en: { title: "", excerpt: "", content: "" } });
const emptyPost = () => ({ slug: "", category: "general", cover_image: "", read_minutes: 5, published: true, translations: emptyTr() });

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [posts, setPosts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [form, setForm] = useState(emptyPost());
  const [tab, setTab] = useState("posts");
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => { setUser(r.data); loadAll(); })
      .catch(() => navigate("/admin/login"))
      .finally(() => setChecking(false));
  }, [navigate]);

  const loadAll = () => {
    api.get("/admin/posts").then((r) => setPosts(r.data)).catch(() => {});
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  };

  const startEdit = (post) => {
    if (post) {
      const tr = emptyTr();
      LANGS.forEach((l) => { if (post.translations?.[l]) tr[l] = post.translations[l]; });
      setForm({ slug: post.slug, category: post.category, cover_image: post.cover_image, read_minutes: post.read_minutes, published: post.published, translations: tr });
      setEditing(post.id);
    } else {
      setForm(emptyPost());
      setEditing("new");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") await api.post("/admin/posts", form);
      else await api.put(`/admin/posts/${editing}`, form);
      toast.success("Saved");
      setEditing(null);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/posts/${id}`).catch(() => {});
    loadAll();
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  const markDone = async (id) => {
    await api.put(`/admin/bookings/${id}`, { status: "done" }).catch(() => {});
    loadAll();
  };

  if (checking) return <main className="min-h-screen bg-cream" data-testid="admin-loading" />;
  if (!user) return null;

  const setTr = (l, field, value) => setForm((f) => ({ ...f, translations: { ...f.translations, [l]: { ...f.translations[l], [field]: value } } }));

  return (
    <main className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="bg-ink text-cream py-4">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-full bg-sage flex items-center justify-center"><Leaf className="w-4 h-4" /></span>
            <span className="font-serif text-lg font-semibold">VITA Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/cs" className="text-sm text-cream/70 hover:text-terra flex items-center gap-1" data-testid="admin-view-site"><Globe className="w-4 h-4" /> Site</a>
            <button onClick={logout} className="text-sm text-cream/70 hover:text-terra flex items-center gap-1" data-testid="admin-logout"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-10">
        <div className="flex gap-2 mb-8">
          <button onClick={() => setTab("posts")} data-testid="admin-tab-posts" className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${tab === "posts" ? "bg-sage text-cream" : "bg-white border border-line"}`}>Posts ({posts.length})</button>
          <button onClick={() => setTab("bookings")} data-testid="admin-tab-bookings" className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${tab === "bookings" ? "bg-sage text-cream" : "bg-white border border-line"}`}>Bookings ({bookings.filter((b) => b.status === "new").length} new)</button>
          <button onClick={() => setTab("stats")} data-testid="admin-tab-stats" className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${tab === "stats" ? "bg-sage text-cream" : "bg-white border border-line"}`}>Статистика</button>
        </div>

        {tab === "posts" && !editing && (
          <div>
            <button onClick={() => startEdit(null)} data-testid="admin-new-post" className="mb-6 inline-flex items-center gap-2 rounded-full bg-sage px-5 py-2.5 text-sm font-semibold text-white hover:bg-ink transition-colors">
              <Plus className="w-4 h-4" /> New post
            </button>
            <div className="bg-white rounded-2xl border border-line divide-y divide-line" data-testid="admin-posts-list">
              {posts.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-4 px-6 py-4" data-testid={`admin-post-row-${p.slug}`}>
                  <div>
                    <p className="font-medium">{p.translations?.ru?.title || p.translations?.cs?.title || p.slug}</p>
                    <p className="text-xs text-muted2">/{p.slug} · {p.category} · {p.published ? "published" : "draft"}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => startEdit(p)} data-testid={`admin-edit-${p.slug}`} className="p-2 rounded-full hover:bg-sand"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(p.id)} data-testid={`admin-delete-${p.slug}`} className="p-2 rounded-full hover:bg-terra-light text-terra"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "posts" && editing && (
          <form onSubmit={save} className="bg-white rounded-2xl border border-line p-8 space-y-5" data-testid="admin-post-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Input required placeholder="slug (url)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} data-testid="post-slug-input" />
              <Input placeholder="category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="post-category-input" />
              <Input placeholder="cover image URL" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} data-testid="post-cover-input" />
              <Input type="number" min="1" placeholder="read minutes" value={form.read_minutes} onChange={(e) => setForm({ ...form, read_minutes: Number(e.target.value) })} data-testid="post-minutes-input" />
            </div>
            <label className="flex items-center gap-3 text-sm" data-testid="post-published-toggle">
              <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} /> Published
            </label>
            <Tabs defaultValue="ru">
              <TabsList>
                {LANGS.map((l) => <TabsTrigger key={l} value={l} data-testid={`post-lang-tab-${l}`}>{l.toUpperCase()}</TabsTrigger>)}
              </TabsList>
              {LANGS.map((l) => (
                <TabsContent key={l} value={l} className="space-y-3 pt-3">
                  <Input placeholder={`Title (${l})`} value={form.translations[l].title} onChange={(e) => setTr(l, "title", e.target.value)} data-testid={`post-title-${l}`} />
                  <Textarea rows={2} placeholder={`Excerpt (${l})`} value={form.translations[l].excerpt} onChange={(e) => setTr(l, "excerpt", e.target.value)} data-testid={`post-excerpt-${l}`} />
                  <Textarea rows={8} placeholder={`Content (${l}) — separate paragraphs with an empty line`} value={form.translations[l].content} onChange={(e) => setTr(l, "content", e.target.value)} data-testid={`post-content-${l}`} />
                </TabsContent>
              ))}
            </Tabs>
            <div className="flex gap-3 pt-2">
              <button type="submit" data-testid="post-save-button" className="rounded-full bg-sage px-6 py-2.5 text-sm font-semibold text-cream hover:bg-sage-dark">Save</button>
              <button type="button" onClick={() => setEditing(null)} data-testid="post-cancel-button" className="rounded-full border border-line px-6 py-2.5 text-sm font-semibold">Cancel</button>
            </div>
          </form>
        )}

        {tab === "bookings" && (
          <div className="bg-white rounded-2xl border border-line divide-y divide-line" data-testid="admin-bookings-list">
            {bookings.length === 0 && <p className="px-6 py-8 text-muted2 text-sm">No booking requests yet.</p>}
            {bookings.map((b) => (
              <div key={b.id} className="px-6 py-4 flex items-start justify-between gap-4" data-testid={`admin-booking-${b.id.slice(0, 8)}`}>
                <div>
                  <p className="font-medium">{b.name} <span className="text-xs font-mono text-muted2 ml-2">[{b.lang}]</span></p>
                  <p className="text-sm text-muted2">{b.contact}{b.service ? ` · ${b.service}` : ""}</p>
                  {b.message && <p className="text-sm mt-1">{b.message}</p>}
                  <p className="text-xs text-muted2 mt-1">{new Date(b.created_at).toLocaleString()}</p>
                </div>
                {b.status === "new" ? (
                  <button onClick={() => markDone(b.id)} data-testid={`admin-booking-done-${b.id.slice(0, 8)}`} className="rounded-full border border-sage text-sage px-4 py-1.5 text-xs font-semibold hover:bg-sage hover:text-cream transition-colors">Mark done</button>
                ) : (
                  <span className="text-xs font-mono text-muted2">done</span>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "stats" && (
          <div data-testid="admin-stats">
            {!stats ? (
              <p className="text-muted2 text-sm">Загрузка…</p>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { id: "views", label: "Просмотры страниц", value: stats.pageviews },
                    { id: "clicks", label: "Клики «Записаться»", value: stats.booking_clicks },
                    { id: "submits", label: "Заявки отправлены", value: stats.booking_submits },
                    { id: "conv", label: "Конверсия в заявку", value: stats.booking_clicks ? Math.round((stats.booking_submits / stats.booking_clicks) * 100) + "%" : "—" },
                  ].map((c) => (
                    <div key={c.id} className="bg-white rounded-2xl border border-line p-5" data-testid={`stat-card-${c.id}`}>
                      <p className="font-serif text-3xl font-semibold text-sage">{c.value}</p>
                      <p className="text-xs text-muted2 mt-1">{c.label}</p>
                    </div>
                  ))}
                </div>
                <div className="grid lg:grid-cols-2 gap-6">
                  <div className="bg-white rounded-2xl border border-line p-6" data-testid="stat-by-day">
                    <h3 className="text-sm font-semibold mb-4">Просмотры по дням (30 дней)</h3>
                    {stats.by_day.length === 0 && <p className="text-sm text-muted2">Пока нет данных — статистика начнёт собираться, как только посетители зайдут на сайт.</p>}
                    <div className="space-y-1.5">
                      {stats.by_day.map(([d, n]) => (
                        <div key={d} className="flex items-center gap-3 text-xs">
                          <span className="w-16 font-mono text-muted2 shrink-0">{d.slice(5)}</span>
                          <div className="h-4 rounded bg-sage/70" style={{ width: `${Math.max(4, (n / stats.max_day) * 70)}%` }} />
                          <span className="font-semibold">{n}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-6">
                    <div className="bg-white rounded-2xl border border-line p-6" data-testid="stat-by-lang">
                      <h3 className="text-sm font-semibold mb-3">По языкам</h3>
                      {stats.by_lang.length === 0 && <p className="text-sm text-muted2">Нет данных</p>}
                      {stats.by_lang.map(([l, n]) => (
                        <div key={l} className="flex justify-between text-sm py-1"><span className="font-mono uppercase">{l}</span><span className="font-semibold">{n}</span></div>
                      ))}
                    </div>
                    <div className="bg-white rounded-2xl border border-line p-6" data-testid="stat-by-ref">
                      <h3 className="text-sm font-semibold mb-3">Источники переходов</h3>
                      {stats.by_ref.length === 0 && <p className="text-sm text-muted2">Нет данных</p>}
                      {stats.by_ref.map(([r, n]) => (
                        <div key={r} className="flex justify-between text-sm py-1"><span className="truncate mr-3">{r}</span><span className="font-semibold">{n}</span></div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-2xl border border-line p-6" data-testid="stat-by-page">
                  <h3 className="text-sm font-semibold mb-3">Просмотры по страницам и статьям</h3>
                  {stats.by_path.length === 0 && <p className="text-sm text-muted2">Нет данных</p>}
                  {stats.by_path.map(([p, n]) => (
                    <div key={p} className="flex justify-between items-center py-1.5 border-b border-line/50 last:border-0">
                      <span className="font-mono text-xs truncate mr-3">{p}</span>
                      <span className="font-semibold text-sm">{n}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
